import React, { useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { ContentContext } from "@/pages/Layout";
import { useApp } from "@/context/AppContext";
import { api, mediaUrl } from "@/lib/api";

const FALLBACK = [
  "https://images.unsplash.com/photo-1552858725-a19e7fcd3ac4?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  "https://images.unsplash.com/photo-1601578318413-af2284f10486?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
];

export default function Home() {
  const content = useContext(ContentContext);
  const { t, loc } = useApp();
  const [products, setProducts] = useState([]);
  const [news, setNews] = useState([]);

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data.slice(0, 4))).catch(() => {});
    api.get("/news").then((r) => setNews(r.data.slice(0, 3))).catch(() => {});
  }, []);

  const hero = content?.hero || {};

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative h-[88vh] min-h-[560px] overflow-hidden">
        <img src={hero.image || FALLBACK[0]} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/65" />
        <div className="relative h-full max-w-[1600px] mx-auto px-6 md:px-12 flex flex-col justify-end pb-20 md:pb-28">
          <p className="overline reveal" style={{ animationDelay: "0.1s" }}>Exclusive Bedding · Since 1998</p>
          <h1 data-testid="hero-title"
            className="reveal mt-5 font-serif text-5xl sm:text-6xl lg:text-[5.5rem] leading-[0.95] text-white max-w-4xl"
            style={{ animationDelay: "0.25s" }}>
            {loc(hero.title) || "Uykunun Aristokrat Hâli"}
          </h1>
          <p className="reveal mt-7 text-base md:text-lg text-neutral-300 max-w-xl font-light"
            style={{ animationDelay: "0.4s" }}>
            {loc(hero.subtitle)}
          </p>
          <div className="reveal mt-10 flex flex-wrap gap-4" style={{ animationDelay: "0.55s" }}>
            <Link to="/products" data-testid="hero-products-btn"
              className="bg-gold text-black px-9 py-3.5 text-[0.72rem] uppercase tracking-[0.24em] font-medium hover:bg-white transition-colors duration-300">
              {t("discover")}
            </Link>
            <Link to="/catalog" data-testid="hero-catalog-btn"
              className="border border-white/25 text-white px-9 py-3.5 text-[0.72rem] uppercase tracking-[0.24em] hover:bg-white/10 hover:border-gold/60 transition-colors duration-300">
              {t("heroCta")}
            </Link>
          </div>
        </div>
      </section>

      {/* MANIFESTO */}
      <section className="px-6 md:px-12 py-24 md:py-36 border-b border-white/5">
        <div className="max-w-[1600px] mx-auto grid grid-cols-1 md:grid-cols-12 gap-14">
          <div className="md:col-span-4">
            <p className="overline">{t("about")}</p>
            <div className="gold-line mt-5 w-24" />
          </div>
          <div className="md:col-span-8">
            <p className="font-serif text-2xl md:text-4xl leading-snug text-neutral-100 font-light">
              {loc(content?.about)?.slice(0, 260)}
            </p>
            <Link to="/about" data-testid="home-about-link"
              className="inline-flex items-center gap-2 mt-10 text-[0.72rem] uppercase tracking-[0.22em] text-gold hover:text-white transition-colors duration-300">
              {t("readMore")} <ArrowUpRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* PRODUCTS */}
      <section className="px-6 md:px-12 py-24 md:py-32">
        <div className="max-w-[1600px] mx-auto">
          <div className="flex items-end justify-between gap-6 mb-14">
            <div>
              <p className="overline">{t("products")}</p>
              <h2 className="font-serif text-3xl sm:text-5xl text-white mt-4">{t("allProducts")}</h2>
            </div>
            <Link to="/products" className="hidden sm:inline text-[0.72rem] uppercase tracking-[0.22em] text-neutral-400 hover:text-gold transition-colors duration-300">
              {t("discover")}
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {(products.length ? products : FALLBACK.map((u, i) => ({ id: `f${i}`, name: "BEDAS", images: [u] })))
              .map((p, i) => (
                <Link to="/products" key={p.id} data-testid={`home-product-${i}`}
                  className="group relative overflow-hidden bg-[#0d0d0d] border border-white/5">
                  <div className="aspect-[3/4] overflow-hidden">
                    <img src={mediaUrl(p.images?.[0]) || FALLBACK[i % 2]} alt={p.name}
                      className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105" />
                  </div>
                  <div className="absolute inset-x-0 bottom-0 p-5 bg-gradient-to-t from-black to-transparent">
                    <p className="text-[0.6rem] uppercase tracking-[0.22em] text-gold">{p.category}</p>
                    <p className="font-serif text-xl text-white mt-1">{p.name}</p>
                  </div>
                </Link>
              ))}
          </div>
        </div>
      </section>

      {/* VISION / MISSION */}
      <section className="px-6 md:px-12 py-24 md:py-32 bg-[#080808] border-y border-white/5">
        <div className="max-w-[1600px] mx-auto grid grid-cols-1 md:grid-cols-2 gap-16 md:gap-24">
          {[["vision", content?.vision], ["mission", content?.mission]].map(([k, v]) => (
            <div key={k} data-testid={`home-${k}`}>
              <p className="overline">{t(k)}</p>
              <div className="gold-line mt-5 w-16" />
              <p className="mt-8 font-serif text-2xl md:text-3xl font-light text-neutral-200 leading-snug">{loc(v)}</p>
            </div>
          ))}
        </div>
      </section>

      {/* NEWS */}
      {news.length > 0 && (
        <section className="px-6 md:px-12 py-24 md:py-32">
          <div className="max-w-[1600px] mx-auto">
            <p className="overline">{t("news")}</p>
            <h2 className="font-serif text-3xl sm:text-5xl text-white mt-4 mb-14">{t("news")}</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {news.map((n, i) => (
                <Link to="/news" key={n.id} data-testid={`home-news-${i}`} className="group border border-white/5 bg-[#0d0d0d]">
                  <div className="aspect-video overflow-hidden">
                    <img src={mediaUrl(n.media?.[0]?.url) || FALLBACK[i % 2]} alt=""
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  </div>
                  <div className="p-6">
                    <p className="text-[0.62rem] uppercase tracking-[0.2em] text-neutral-500">{n.date}</p>
                    <p className="font-serif text-2xl text-white mt-2">{n.title}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
